import React, { useState, useEffect } from 'react'
import AppBar from '@mui/material/AppBar'
import { useLocation } from 'react-router'
import { styled } from '@mui/material/styles'

import { useBrandingContext } from '../../context'
import { Container } from '..'
import logo from './givewise-logo-wordmark.png'

const LogoContainer = styled('div')(() => ({
  textAlign: 'center',
  display: 'block',

  '& img': {
    maxWidth: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    objectPosition: 'center',
  },
}))

export function Header() {
  const { pathname } = useLocation()
  const isCharitySignUpPage = pathname.indexOf('/charities') > -1 && pathname.indexOf('/claim') > -1
  const logoAlign = isCharitySignUpPage ? 'left' : 'center'
  const branding = useBrandingContext()
  const [imageShape, setImageShape] = useState('square')

  // Check if header logo should be shown (defaults to true if not explicitly set to false)
  const showHeaderLogo = branding?.showHeaderLogo !== false

  // Fallback to public path if webpack import fails
  const fallbackLogo = '/givewise-logo-wordmark.png'
  const logoSrc = branding?.getAssetValue('logo') || logo || fallbackLogo
  const logoKey = branding?.brandingKey
  const logoDefault = 'givewise'
  const logoRedirectUrl = branding?.logoRedirectUrl || 'https://www.givewise.ca'

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const aspectRatio = img.width / img.height
      if (logoKey === logoDefault) {
        setImageShape('default')
      } else if (aspectRatio > 1.2) {
        setImageShape('landscape')
      } else if (aspectRatio < 0.8) {
        setImageShape('vertical')
      } else {
        setImageShape('square')
      }
    }
    img.src = logoSrc
  }, [logoSrc])

  const getImageStyles = () => {
    switch (imageShape) {
      case 'default':
        return {
          maxHeight: '40px',
        }
      case 'vertical':
        return {
          maxHeight: '90px',
        }
      case 'landscape':
        return {
          maxHeight: '70px',
        }
      case 'square':
      default:
        return {
          maxHeight: '80px',
        }
    }
  }

  if (!showHeaderLogo) {
    return null
  }

  return (
    <AppBar
      // Stable hook for chrome-hiding modes (e.g. Inquiry focus mode keys off
      // `body.inquiry-focus .app-header` in index.css).
      className="app-header"
      position="static"
      sx={{
        boxShadow: 'none',
        backgroundColor: 'var(--lightest-grey)',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          mb: 0,
          padding: isCharitySignUpPage ? '25px' : 'auto',
        }}
        data-testid="logo"
      >
        <LogoContainer
          as="a"
          href={logoRedirectUrl}
          sx={{
            textAlign: logoAlign,
            margin: '0.3rem auto',
            ...getImageStyles(),
          }}
        >
          <img
            src={logoSrc}
            alt="GiveWise Home"
            loading="eager"
            style={{
              maxHeight: getImageStyles().maxHeight,
            }}
            onError={(e) => {
              // If current source fails, try the public path
              if (e.target.src !== fallbackLogo) {
                e.target.src = fallbackLogo
              }
            }}
          />
          <span className="sr-only">{branding?.getAssetValue('displayName')} Home</span>
        </LogoContainer>
      </Container>
    </AppBar>
  )
}
